import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Mail, Send, FileText, BarChart3 } from 'lucide-react'

import AdminHeader from '../../../shared/components/AdminHeader'
import { adminEmailAPI } from '../../../shared/services/emailAPI'

import EmailOverviewTab from '../components/email/EmailOverviewTab'
import EmailLogsTab from '../components/email/EmailLogsTab'
import EmailTemplatesTab from '../components/email/EmailTemplatesTab'
import EmailSendTab from '../components/email/EmailSendTab'
import type { ActiveTab } from '../components/email/types'

export default function EmailManagementPage() {
  const [activeTab, setActiveTab] = useState<ActiveTab>('overview')
  const [filters, setFilters] = useState<any>({}) // 필터 기능 구현 시 사용
  const queryClient = useQueryClient()

  // 이메일 통계 데이터
  const { data: emailStats, isLoading: statsLoading } = useQuery({
    queryKey: ['emailStats', 30],
    queryFn: () => adminEmailAPI.getStats(30),
  })

  // 이메일 로그 데이터
  const { data: emailLogsData, isLoading: logsLoading } = useQuery({
    queryKey: ['emailLogs', filters],
    queryFn: () => adminEmailAPI.getLogs(filters),
  })

  // 이메일 템플릿 데이터
  const { data: emailTemplatesData, isLoading: templatesLoading } = useQuery({
    queryKey: ['emailTemplates'],
    queryFn: () => adminEmailAPI.getTemplates(),
  })

  // 메일 발송 mutation
  const sendEmailMutation = useMutation({
    mutationFn: adminEmailAPI.sendEmail,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailLogs'] })
      queryClient.invalidateQueries({ queryKey: ['emailStats'] })
    },
  })

  // 템플릿 삭제 mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: adminEmailAPI.deleteTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['emailTemplates'] })
    },
  })

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <AdminHeader
        title="메일 관리"
        subtitle="발송된 메일 확인 및 새로운 메일 템플릿 관리"
      />

      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* 탭 네비게이션 */}
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'overview', name: '개요', icon: BarChart3 },
                { id: 'logs', name: '메일 로그', icon: Mail },
                { id: 'templates', name: '템플릿', icon: FileText },
                { id: 'send', name: '메일 발송', icon: Send },
              ].map(tab => {
                const Icon = tab.icon
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as ActiveTab)}
                    className={`flex items-center border-b-2 px-1 py-2 text-sm font-medium ${
                      activeTab === tab.id
                        ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                        : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300'
                    }`}
                  >
                    <Icon className="mr-2 h-4 w-4" />
                    {tab.name}
                  </button>
                )
              })}
            </nav>
          </div>

          {/* 탭 컨텐츠 */}
          <div className="mt-6">
            {activeTab === 'overview' && (
              <EmailOverviewTab stats={emailStats} loading={statsLoading} />
            )}
            {activeTab === 'logs' && (
              <EmailLogsTab
                logs={emailLogsData?.logs || []}
                pagination={emailLogsData?.pagination}
                loading={logsLoading}
                filters={filters}
                setFilters={setFilters}
              />
            )}
            {activeTab === 'templates' && (
              <EmailTemplatesTab
                templates={emailTemplatesData?.templates || []}
                pagination={emailTemplatesData?.pagination}
                loading={templatesLoading}
                onDeleteTemplate={id => deleteTemplateMutation.mutate(id)}
                deleteLoading={deleteTemplateMutation.isPending}
              />
            )}
            {activeTab === 'send' && (
              <EmailSendTab
                onSendEmail={sendEmailMutation.mutate}
                sendLoading={sendEmailMutation.isPending}
                templates={emailTemplatesData?.templates || []}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// 이메일 개요 탭
